import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Search } from "lucide-react";

const TYPE_LABEL = {
  annonceur: "Annonceur",
  partenaire: "Partenaire",
  lecteur_premium: "Lecteur premium",
  abonne: "Abonné",
  prospect: "Prospect",
};
const STATUS_LABEL = {
  nouveau_lead: "Nouveau lead",
  en_discussion: "En discussion",
  proposal_sent: "Devis envoyé",
  qualified: "Qualifié",
  proposal_accepted: "Client actif",
  proposal_declined: "Client perdu",
  closed: "Fermé",
  client_actif: "Client actif",
  new: "Nouveau",
  pending: "En attente",
  confirmed: "Confirmé",
  declined: "Décliné",
  contacted: "Contacté",
};

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/clients")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (!needle) return true;
      const bag = [r.full_name, r.email, r.company, r.phone, r.country].join(" ").toLowerCase();
      return bag.includes(needle);
    });
  }, [rows, q, typeFilter]);

  const counts = useMemo(() => {
    const m = { all: rows.length };
    rows.forEach((r) => { m[r.type] = (m[r.type] || 0) + 1; });
    return m;
  }, [rows]);

  return (
    <div data-testid="page-admin-clients">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Relation commerciale</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Clients</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)]">
            Annonceurs, abonnés, partenaires, lecteurs premium et prospects — vue unifiée.
          </p>
        </div>
        <div className="text-sm text-[var(--gai-grey-600)]">
          <span className="serif text-2xl gai-gradient-gold">{rows.length}</span> contacts au total
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gai-grey-400)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher — nom, email, société…"
            className="gai-input pl-9"
            data-testid="clients-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "annonceur", "partenaire", "lecteur_premium", "abonne", "prospect"].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border ${
                typeFilter === t
                  ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                  : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-gold)]"
              }`}
              data-testid={`clients-filter-${t}`}
            >
              {t === "all" ? "Tous" : TYPE_LABEL[t]} <span className="ml-1 text-[0.65rem] opacity-60">{counts[t] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="gai-card gai-sharp overflow-hidden" data-testid="clients-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.68rem] tracking-[0.2em] uppercase text-[var(--gai-grey-500)]" style={{ background: "var(--gai-grey-50)" }}>
              <th className="py-3 px-5">Client</th>
              <th className="py-3 px-5">Contact</th>
              <th className="py-3 px-5">Type</th>
              <th className="py-3 px-5">Statut</th>
              <th className="py-3 px-5">Source</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-[var(--gai-grey-500)]">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[var(--gai-grey-500)]">Aucun résultat.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={`${r.source}-${r.id}`} className="border-t" style={{ borderColor: "var(--gai-grey-200)" }} data-testid={`client-row-${r.source}-${r.id}`}>
                <td className="py-3 px-5">
                  <div className="text-[var(--gai-black)] font-medium">{r.full_name || "—"}</div>
                  {r.company && <div className="text-xs text-[var(--gai-grey-500)] mt-0.5">{r.company}</div>}
                </td>
                <td className="py-3 px-5 text-[var(--gai-grey-700)]">
                  <div>{r.email || "—"}</div>
                  {r.phone && <div className="text-xs text-[var(--gai-grey-500)]">{r.phone}</div>}
                </td>
                <td className="py-3 px-5">
                  <span className="text-[0.7rem] tracking-[0.18em] uppercase px-2 py-1 border" style={{ borderColor: "var(--gai-red)", color: "var(--gai-gold)" }}>
                    {TYPE_LABEL[r.type] || r.type}
                  </span>
                </td>
                <td className="py-3 px-5 text-[0.72rem] tracking-[0.12em] uppercase text-[var(--gai-grey-700)]">
                  {STATUS_LABEL[r.status] || r.status}
                </td>
                <td className="py-3 px-5 text-[var(--gai-grey-500)] text-xs">{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
